import { useCallback, useEffect, useRef } from "react";

const DEFAULT_MS = 550;
const MOVE_CANCEL_PX = 14;

/**
 * На тач-устройствах долгое нажатие вызывает тот же обработчик, что и контекстное меню (ПКМ).
 * Короткий тап и заметный сдвиг отменяют таймер; после long-press подавляется следующий клик по дереву.
 */
export function useLongPressContextMenu(onMenu, options = {}) {
  const durationMs = options.durationMs ?? DEFAULT_MS;
  const moveCancelPx = options.moveCancelPx ?? MOVE_CANCEL_PX;

  const menuRef = useRef(onMenu);
  menuRef.current = onMenu;

  const timerRef = useRef(null);
  const originRef = useRef({ x: 0, y: 0 });
  const suppressUntilRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearTimer(), [clearTimer]);

  const fireMenu = useCallback((clientX, clientY) => {
    const fn = menuRef.current;
    if (!fn) return;
    suppressUntilRef.current = Date.now() + 480;
    try {
      navigator.vibrate?.(12);
    } catch {
      // ignore
    }
    fn({
      preventDefault() {},
      stopPropagation() {},
      clientX,
      clientY,
    });
  }, []);

  const consumeClickIfSuppressed = useCallback(() => {
    if (Date.now() < suppressUntilRef.current) {
      suppressUntilRef.current = 0;
      return true;
    }
    return false;
  }, []);

  const wrapParentClickCapture = useCallback(
    (e) => {
      if (consumeClickIfSuppressed()) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [consumeClickIfSuppressed]
  );

  const onContextMenu = useCallback((e) => {
    const fn = menuRef.current;
    if (!fn) return;
    e?.preventDefault?.();
    fn(e);
  }, []);

  const onPointerDown = useCallback(
    (e) => {
      const fn = menuRef.current;
      if (!fn || e.pointerType === "mouse" || e.button !== 0) return;
      clearTimer();
      originRef.current = { x: e.clientX, y: e.clientY };
      const x = e.clientX;
      const y = e.clientY;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        fireMenu(x, y);
      }, durationMs);
    },
    [clearTimer, durationMs, fireMenu]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (timerRef.current == null || e.pointerType === "mouse") return;
      const dx = Math.abs(e.clientX - originRef.current.x);
      const dy = Math.abs(e.clientY - originRef.current.y);
      if (dx > moveCancelPx || dy > moveCancelPx) clearTimer();
    },
    [clearTimer, moveCancelPx]
  );

  const onPointerEnd = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  if (!onMenu) {
    return {
      handlers: {},
      wrapParentClickCapture: undefined,
      consumeClickIfSuppressed,
    };
  }

  return {
    handlers: {
      onContextMenu,
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
      onPointerLeave: onPointerEnd,
    },
    wrapParentClickCapture,
    consumeClickIfSuppressed,
  };
}
